import { createApp } from 'vue';
import { createPinia } from 'pinia';
import * as Sentry from '@sentry/vue';
import TrioAssets from 'trio-assets-js';
import { SENTRY_DSN } from './js/utils/constants';

// Vue components.
// Crops Stage Program.
import CropStageProgram from './js/components/crop-stage-program/CropStageProgram';

// Elements.
import SustainabilityPie from './js/components/elements/SustainabilityPie';

// Products.
import ProductFinder from './js/components/products/ProductFinder';
import RelatedProducts from './js/components/products/RelatedProducts';
import SustainableApplicationExplanation from './js/components/products/SustainableApplicationExplanation';

// Sales contacts.
import SalesContactFinder from './js/components/sales-contacts/SalesContactFinder';
import SalesContactWidget from './js/components/sales-contacts/SalesContactWidget';

// Search.
import SearchPage from './js/components/search/SearchPage';
import SearchWidget from './js/components/search/SearchWidget';

// Misc.
import ApplicationRecommendation from './js/components/ApplicationRecommendation';
import CollaborationsMap from './js/components/CollaborationsMap';
import CropsFinder from './js/components/CropsFinder';
import Downloads from './js/components/Downloads';
import HeroSlider from './js/components/HeroSlider';
import IconSlider from './js/components/IconSlider';
import SocialMediaSlider from './js/components/SocialMediaSlider';
import DownloadPasswordProtectedMedia from './js/components/DownloadPasswordProtectedMedia';

// Styling.
import './scss/app.scss';

// Utils.
import './js/utils/momentLocalUpdates';

// Directives.
// @todo check if we still need/use?
import clickOutside from './js/directives/clickOutside';

// Blocks.
import './js/blocks/contactWidget';
import './js/blocks/mainMenu';
import './js/blocks/productSiblingTerms';

// Elements.
import './js/elements/carousel';
import './js/elements/categorySelect';
import './js/elements/dialogModal';
import './js/elements/form';
import './js/elements/linktree';
import './js/elements/mediaSlider';
import './js/elements/productDetails';
import './js/elements/scrollToElement';

// Paragraphs.
import './js/paragraphs/accordion';
import './js/paragraphs/lottery-webform';
import './js/paragraphs/tabs';
import './js/paragraphs/testimonial';
import './js/paragraphs/timeline';
// Partials.
import './js/partials/jumpmarkMenu';

(() => {
  TrioAssets.parallax();
})();

// Vue apps initialization.
// Prepare an array of apps for Sentry later.
const app = [];

// A list of all top-level components (app instances).
const appIslands = [
  {
    id: 'application-recommendation',
    component: {
      name: 'ApplicationRecommendation',
      instance: ApplicationRecommendation,
    },
  },
  {
    id: 'collaborations-map',
    component: {
      name: 'CollaborationsMap',
      instance: CollaborationsMap,
    },
  },
  {
    id: 'crop-stage-program',
    component: {
      name: 'CropStageProgram',
      instance: CropStageProgram,
    },
  },
  {
    id: 'crops-finder',
    component: {
      name: 'CropsFinder',
      instance: CropsFinder,
    },
  },
  {
    id: 'downloads',
    component: {
      name: 'Downloads',
      instance: Downloads,
    },
  },
  {
    id: 'hero-slider',
    component: {
      name: 'HeroSlider',
      instance: HeroSlider,
    },
  },
  {
    id: 'product-finder',
    component: {
      name: 'ProductFinder',
      instance: ProductFinder,
    },
    directives: {
      'click-outside': clickOutside,
    },
    hasStore: true,
  },
  {
    id: 'related-products',
    component: {
      name: 'RelatedProducts',
      instance: RelatedProducts,
    },
  },
  {
    id: 'sales-contacts-finder',
    component: {
      name: 'SalesContactFinder',
      instance: SalesContactFinder,
    },
  },
  {
    id: 'search-page',
    component: {
      name: 'SearchPage',
      instance: SearchPage,
    },
  },
  {
    id: 'block-exposed-form-search-block',
    component: {
      name: 'SearchWidget',
      instance: SearchWidget,
    },
  },
  {
    id: 'social-media-slider',
    component: {
      name: 'SocialMediaSlider',
      instance: SocialMediaSlider,
    },
  },
  {
    id: 'sales-contact-widget',
    component: {
      name: 'SalesContactWidget',
      instance: SalesContactWidget,
    },
  },
  {
    id: 'download-password-protected-media',
    component: {
      name: 'DownloadPasswordProtectedMedia',
      instance: DownloadPasswordProtectedMedia,
    },
  },
  {
    id: 'sustainable-application',
    component: {
      name: 'SustainableApplicationExplanation',
      instance: SustainableApplicationExplanation,
      children: [{ name: 'SustainabilityPie', instance: SustainabilityPie }],
    },
  },
  {
    id: 'sustainable-application-pie',
    component: {
      name: 'SustainabilityPie',
      instance: SustainabilityPie,
    },
  },
];

// Apps initialization.
appIslands.forEach((island) => {
  const element = document.getElementById(island.id);
  if (element) {
    const appInstance = createApp({
      data() {
        return {};
      },
    });
    appInstance.component(island.component.name, island.component.instance);

    // Register global (to this app instance) components if any.
    if (island?.component?.children) {
      island.component.children.forEach((child) => {
        appInstance.component(child.name, child.instance);
      });
    }

    // Register directives if any.
    if (island?.directives) {
      Object.entries(island.directives).forEach(([name, directive]) => {
        appInstance.directive(name, directive);
      });
    }

    // Create a Pinia instance if needed.
    if (island.hasStore) {
      appInstance.use(createPinia());
    }
    appInstance.mount(element);
    app.push(appInstance);
  }
});

// Icon slider paragraphs apps.
const paragraphs = document.querySelectorAll('.paragraph--icon-slider');
if (paragraphs.length) {
  paragraphs.forEach((paragraph) => {
    const appInstance = createApp({
      data() {
        return {};
      },
    })
      .component('IconSlider', IconSlider)
      .mount(paragraph);
    app.push(appInstance);
  });
}

// scroll page to form for Multistep form.
document.addEventListener('DOMContentLoaded', () => {
  const urlParams = new URLSearchParams(window.location.search);
  const page = urlParams.get('page');

  // Scroll only if we're on a wizard page
  if (page) {
    const form = document.querySelector('.webform-submission-form'); // adjust selector if needed
    if (form) {
      // Delay a bit to ensure everything renders
      setTimeout(() => {
        form.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 200); // You can tweak this value
    }
  }
});

// Sentry integration.
let siteEnv = 'development';
let sampleRate = 1.0;

if (
  document.location.host === 'compo-expert.com' ||
  document.location.host === 'www.compo-expert.com'
) {
  siteEnv = 'production';
  sampleRate = 0.1;
} else if (document.location.host === 'compo-expert.trio-preview.de') {
  siteEnv = 'staging';
  sampleRate = 0.3;
}

Sentry.init({
  // Passing all initialized apps to Sentry.
  app,
  dsn: SENTRY_DSN,
  environment: siteEnv,
  tracesSampleRate: sampleRate,
  tracingOptions: {
    trackComponents: true,
  },
});
